import api from './api';
import { API_ENDPOINTS } from '../constants';
import { ApiResponse } from '../types';

// Simple in-memory cache
class CacheService {
  private cache = new Map<string, { data: any; timestamp: number; ttl: number }>();

  set(key: string, data: any, ttl: number = 300000) { // 5 minutes default
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  get(key: string): any | null {
    const item = this.cache.get(key);
    if (!item) return null;

    const now = Date.now();
    if (now - item.timestamp > item.ttl) {
      this.cache.delete(key);
      return null;
    }

    return item.data;
  }

  clear() {
    this.cache.clear();
  }

  delete(key: string) {
    this.cache.delete(key);
  }
}

const cacheService = new CacheService();

export interface DashboardOverview {
  totalUsers: number;
  totalStores: number;
  totalClusters: number;
  activeUsers: number;
  todayAttendance: number;
  attendanceRate: number;
}

export interface StoreStatistic {
  id: string;
  name: string;
  code: string;
  city: string;
  state: string;
  manager: string;
  cluster: string;
  staffCount: number;
  attendanceRate: number | null;
  todayAttendance?: number; // Add optional field
}

export interface RecentActivity {
  type: string;
  message: string;
  store: string;
  timestamp?: string; // Add optional field
}

export interface Notification {
  id: string;
  type: 'warning' | 'info' | 'error' | 'success';
  title: string;
  message: string;
  priority: 'high' | 'medium' | 'low';
}

export interface ChartData {
  userRoleDistribution: { role: string; count: number }[];
  storePerformance: { storeName: string; staffCount: number; attendanceRate: number }[];
  attendanceTrend: { date: string; attendance: number; rate: number }[];
}

export interface BreakViolation {
  user_id: string;
  employee_id: string;
  name: string;
  date: string;
  total_break_time: number;
  excess_time: number;
  store: {
    id: string;
    name: string;
    code: string;
  } | null;
  cluster: {
    id: string;
    name: string;
    code: string;
  } | null;
  breaks_count: number;
}

export interface DashboardData {
  overview: DashboardOverview;
  storeStatistics: StoreStatistic[];
  recentActivities: RecentActivity[];
  notifications: Notification[];
  breakViolations?: BreakViolation[];
  charts: ChartData;
}

const dashboardService = {
  getDashboardData: async (): Promise<DashboardData> => {
    const cacheKey = 'dashboard_data';
    
    // Try to get from cache first
    const cachedData = cacheService.get(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    try {
      const response = await api.get<ApiResponse<DashboardData>>(API_ENDPOINTS.DASHBOARD.DATA);
      const data = response.data.data!;
      
      // Cache the data for 2 minutes
      cacheService.set(cacheKey, data, 120000);
      
      return data;
    } catch (error) {
      console.error('Dashboard API error:', error);
      throw error;
    }
  },

  // Clear cache when data is updated
  clearCache: () => {
    cacheService.clear();
  },

  // Clear specific cache key
  clearDashboardCache: () => {
    cacheService.delete('dashboard_data');
  }
};

export default dashboardService;